import {
  CountryCode,
  MedicationProductCodeKey,
  MedicationProductName,
  OTCMedicationProductCodeKey,
  OTCMedicationProductUS,
  RxMedicationProductCodeKey,
  RxMedicationProductUS,
  RxMedicationProductUK,
  STIMedicationProductUK,
  STIMedicationProductCodeKey,
  DTResultParameterType
} from '@dayetopia/library/types';
import { Medication, TreatmentTypeData, TreatmentTypes } from '@contracts';
import Step1 from '@images/vms-account/vms-how-to/treatment-step1.svg';
import Step2 from '@images/vms-account/vms-how-to/treatment-step2.svg';
import Step3 from '@images/vms-account/vms-how-to/treatment-step3.svg';
import Capsules from '@images/vms-account/vms-icons/Capsules.svg';
import Cream from '@images/vms-account/vms-icons/Cream.svg';
import Suppository from '@images/vms-account/vms-icons/Suppository.svg';
import { isCountryMatch } from '@utils/helpers';

export interface TreatmentData {
  id: MedicationProductCodeKey;
  title: MedicationProductName;
  medication: string;
  country?: string;
  subtitle: string;
  type: string;
  description: string;
  instructions: string;
  icon: JSX.Element;
  price?: string;
  priceUS?: string;
  reviewFee?: string;
  reviewFeeUS?: string;
  isOTC?: boolean;
  isSTI?: boolean;
}

interface TreatmentOptions {
  types?: string[];
  ids?: string[];
  medications?: string[];
  country?: CountryCode;
  isSTI?: boolean;
}

export const getTreatments = (options?: TreatmentOptions): TreatmentData[] =>
  treatmentsData.filter((treatment) => {
    let nameMatch = true;
    let typeMatch = true;
    let countryMatch = true;
    let medicationsMatch = true;
    let stiMatch = true;

    if (options?.ids && options?.ids.length) {
      nameMatch = options?.ids.includes(treatment.id);
    }

    if (options?.types && options?.types.length) {
      typeMatch = options?.types.includes(treatment.type);
    }

    if (options?.medications && options?.medications.length) {
      medicationsMatch = options?.medications.includes(treatment.medication);
    }

    if (options?.country && treatment.country) {
      countryMatch = !!treatment.country && isCountryMatch(treatment.country, options?.country);
    }

    if (options?.isSTI !== undefined) {
      stiMatch = treatment.isSTI == null || treatment.isSTI === options.isSTI;
    }

    return nameMatch && typeMatch && countryMatch && medicationsMatch && stiMatch;
  });

export function getTreatmentById(id?: MedicationProductCodeKey | null) {
  return treatmentsData.find((t) => t.id === id);
}

export function getTreatmentSteps(country: CountryCode): { text: string; icon: JSX.Element; step: number }[] {
  return [
    {
      text:
        country !== CountryCode.US
          ? 'When you make a treatment request, we share your symptoms and medical history with our pharmacy partner.'
          : 'You send us a request with your symptoms and preferred treatment.',
      icon: Step1,
      step: 1
    },
    {
      text:
        country !== CountryCode.US
          ? 'A pharmacist reviews your request within 1-2 business days and prescribes one of the treatments listed below that is suitable for you.'
          : 'A doctor or pharmacist will review your request and will prescribe the treatment if it’s suitable for you.',
      icon: Step2,
      step: 2
    },
    {
      text: 'You get medication delivered discretely to your home.',
      icon: Step3,
      step: 3
    }
  ];
}

export const treatmentsData: TreatmentData[] = [
  {
    id: OTCMedicationProductCodeKey.ClotrimazoleCream15g,
    title: OTCMedicationProductUS.clotrimazole_cream_15g,
    medication: Medication.THRUSH,
    country: 'US',
    subtitle: 'Cream • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>Apply the cream thinly and evenly to the affected area 2-3 times a day.</strong></p>' +
      '<p>You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.</p>' +
      '<p><strong>Clotrimazole may damage latex contraceptives, so please use other contraceptive precautions for at least 5 days after using this product.</strong></p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine.</p>',
    instructions:
      'The cream should be applied thinly and evenly to the affected area 2 – 3 times a day and rubbed in gently. You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.',
    icon: Cream,
    priceUS: '18.29',
    reviewFeeUS: '6.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.ClotrimazoleCream20g,
    title: RxMedicationProductUK.clotrimazole_cream_20g,
    medication: Medication.THRUSH,
    country: '-US',
    subtitle: 'Cream • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>Apply the cream thinly and evenly to the affected area 2-3 times a day.</strong></p>' +
      '<p>You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.</p>' +
      '<p><strong>Clotrimazole may damage latex contraceptives, so please use other contraceptive precautions for at least 5 days after using this product.</strong></p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine.</p>',
    instructions:
      'The cream should be applied thinly and evenly to the affected area 2 – 3 times a day and rubbed in gently. You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.',
    icon: Cream,
    reviewFee: '3.00',
    price: '5.29',
    isOTC: true
  },
  {
    id: OTCMedicationProductCodeKey.ClotrimazoleCream21g,
    title: OTCMedicationProductUS.clotrimazole_cream_21g,
    medication: Medication.THRUSH,
    country: 'US',
    subtitle: 'Cream • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>Apply the cream thinly and evenly to the affected area 2-3 times a day.</strong></p>' +
      '<p>You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.</p>' +
      '<p><strong>Clotrimazole may damage latex contraceptives, so please use other contraceptive precautions for at least 5 days after using this product.</strong></p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine.</p>',
    instructions:
      'The cream should be applied thinly and evenly to the affected area 2 – 3 times a day and rubbed in gently. You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.',
    icon: Cream,
    priceUS: '15.49',
    reviewFeeUS: '6.99',
    isOTC: true
  },
  {
    id: OTCMedicationProductCodeKey.ClotrimazoleCream29g,
    title: OTCMedicationProductUS.clotrimazole_cream_29g,
    medication: Medication.THRUSH,
    country: 'US',
    subtitle: 'Cream • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>Apply the cream thinly and evenly to the affected area 2-3 times a day.</strong></p>' +
      '<p>You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.</p>' +
      '<p><strong>Clotrimazole may damage latex contraceptives, so please use other contraceptive precautions for at least 5 days after using this product.</strong></p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine.</p>',
    instructions:
      'The cream should be applied thinly and evenly to the affected area 2 – 3 times a day and rubbed in gently. You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.',
    icon: Cream,
    priceUS: '21.29',
    reviewFeeUS: '6.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.ClotrimazoleCream50g,
    title: RxMedicationProductUK.clotrimazole_cream_50g,
    medication: Medication.THRUSH,
    country: '-US',
    subtitle: 'Cream • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>Apply the cream thinly and evenly to the affected area 2-3 times a day.</strong></p>' +
      '<p>You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.</p>' +
      '<p><strong>Clotrimazole may damage latex contraceptives, so please use other contraceptive precautions for at least 5 days after using this product.</strong></p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine.</p>',
    instructions:
      'The cream should be applied thinly and evenly to the affected area 2 – 3 times a day and rubbed in gently. You should continue with treatment throughout the prescribed duration, even if an infection has visibly cleared, as Candida spores may remain and cause a recurrent infection.',
    icon: Cream,
    reviewFee: '3.00',
    price: '6.49',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.Clotrimazole500mg,
    title: RxMedicationProductUK.clotrimazole_500mg,
    medication: Medication.THRUSH,
    country: '-US',
    subtitle: '1 suppository • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>The pessary should be inserted into the vagina, as high as possible, using the provided applicator.</strong></p>' +
      '<p>Pessaries need moisture in the vagina to dissolve fully. If you are experiencing dryness, you might find undissolved pieces of the pessary. It may help to insert it as high as possible into the vagina before you go to bed. Make sure you are not on your period, as menstrual fluid can wash out the medication. Do not use tampons, intravaginal douches, spermicides or other vaginal products while using this product. If you are pregnant, you should insert the pessary without using an applicator.</p>' +
      '<p><strong>Clotrimazole may damage latex contraceptives, so please use other contraceptive precautions for at least 5 days after using this product.</strong></p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine.</p>',
    instructions: 'The pessary should be inserted into the vagina, as high as possible, using the provided applicator.',
    reviewFee: '3.00',
    icon: Suppository,
    price: '9.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.Fluconazole150mg,
    title: RxMedicationProductUK.fluconazole_150mg,
    medication: Medication.THRUSH,
    subtitle: '1 capsule • Antifungal',
    type: TreatmentTypes.Thrush,
    description:
      '<p><strong>Swallow the capsule whole with a drink of water.</strong></p>' +
      '<p>Your fungal infection should start to clear up within a few days of taking the capsule and you may notice an improvement in just one day. This treatment is not suitable for use by children under the age of 16 or adults over the age of 60.</p>' +
      '<p>Please make sure you read the information leaflet carefully before using this medicine, especially if you started medication other than what you’ve already specified, please make sure you read the leaflet carefully before taking the capsule.</p>',
    instructions: 'Swallow the capsule whole with a drink of water.',
    icon: Capsules,
    price: '9.29',
    priceUS: '24.95',
    reviewFee: '3.00',
    reviewFeeUS: '24.99',
    isOTC: true
  },
  {
    id: OTCMedicationProductCodeKey.BoricAcidCapsules,
    title: OTCMedicationProductUS.boric_acid_capsules,
    medication: Medication.BORICACID,
    country: 'US',
    subtitle: '',
    type: '',
    description: '',
    instructions: '',
    icon: Capsules,
    priceUS: '19.95',
    reviewFeeUS: '6.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.Flagyl500mg,
    title: RxMedicationProductUS.flagyl_500mg,
    medication: Medication.BV,
    country: 'US',
    subtitle: '',
    type: '',
    description: '',
    instructions: '',
    icon: Capsules,
    priceUS: '14.29',
    reviewFeeUS: '24.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.ZidovalGel,
    title: RxMedicationProductUS.zidoval_gel,
    medication: Medication.BV,
    country: 'US',
    subtitle: '',
    type: '',
    description: '',
    instructions: '',
    icon: Cream,
    priceUS: '39.49',
    reviewFeeUS: '24.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.Cleocin40g,
    title: RxMedicationProductUS.cleocin_40g,
    medication: Medication.BV,
    country: 'US',
    subtitle: '',
    type: '',
    description: '',
    instructions: '',
    icon: Cream,
    priceUS: '84.95',
    reviewFeeUS: '24.99',
    isOTC: true
  },
  {
    id: RxMedicationProductCodeKey.Relactagel,
    title: RxMedicationProductUK.relactagel,
    medication: Medication.BV,
    country: '-US',
    subtitle: 'Gel • Non-antibiotic',
    type: TreatmentTypes.Bv,
    description:
      '<p><strong>Use 1 single tube daily for 7 days at bedtime.</strong></p>' +
      '<p>Relactagel is a lactic acid gel that is clinically proven to treat and prevent bacterial vaginosis (BV) and relieve symptoms.</p>' +
      '<p>Lactate gels restore the natural acidity of the vagina, providing an environment favourable to the growth of the naturally occurring protective bacteria, the lactobacilli. Relactagel also contains glycogen which act as nutrients for lactobacilli and support their growth.</p>',
    instructions: 'Use 1 single tube daily for 7 days at bedtime.',
    icon: Cream,
    price: '13.95',
    reviewFee: '3.00',
    isOTC: true
  },
  {
    id: STIMedicationProductCodeKey.Doxycycline100mg,
    title: STIMedicationProductUK.doxycycline_100mg,
    medication: Medication.CHLAMYDIA,
    country: '-US',
    subtitle: 'Antibiotic',
    type: 'Chlamydia treatment',
    description: '',
    instructions: '',
    icon: Cream,
    price: '18.00',
    reviewFee: '10.00',
    isOTC: false,
    isSTI: true
  },
  {
    id: STIMedicationProductCodeKey.Azithromycin500mg,
    title: STIMedicationProductUK.azithromycin_500mg,
    medication: Medication.CHLAMYDIA,
    country: '-US',
    subtitle: 'Antibiotic',
    type: 'Chlamydia treatment',
    description: '',
    instructions: '',
    icon: Cream,
    price: '18.00',
    reviewFee: '10.00',
    isOTC: false,
    isSTI: true
  },
  {
    id: STIMedicationProductCodeKey.Metronidazole400mg,
    title: STIMedicationProductUK.metronidazole_400mg,
    medication: Medication.TRICHOMONIASIS,
    country: '-US',
    subtitle: 'Antibiotic',
    type: 'Trichomoniasis treatment',
    description: '',
    instructions: '',
    icon: Cream,
    price: '19.00',
    reviewFee: '10.00',
    isOTC: false,
    isSTI: true
  },
  {
    id: STIMedicationProductCodeKey.CombinedDoxycycline100mgAzithromycin500mg,
    title: STIMedicationProductUK.combined_doxycycline_100mg_azithromycin_500mg,
    medication: Medication.MYCOPLASMA_GENITALIUM,
    country: '-US',
    subtitle: 'Antibiotic',
    type: 'M. genitalium treatment',
    description: '',
    instructions: '',
    icon: Cream,
    price: '29.99',
    reviewFee: '10.00',
    isOTC: false,
    isSTI: true
  }
  // TODO: Check if we want to add Moxifloxacin tablets 400mg (7 doses)
];

export const treatmentTypesData: TreatmentTypeData[] = [
  {
    id: TreatmentTypes.Bv,
    name: 'Treatment for BV',
    pathogenId: DTResultParameterType.ANAEROBIC_MICROORGANISMS,
    icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZhbQXTjCgu4jzu8z_BVTreatment%2BMed.svg'
  },
  {
    id: TreatmentTypes.Thrush,
    name: 'Treatment for Thrush',
    pathogenId: DTResultParameterType.CANDIDA,
    icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZhbQWzjCgu4jzu8x_CandidaTreatment%2BMed.svg'
  },
  {
    id: TreatmentTypes.Ureaplasma,
    name: 'Ureaplasma Treatment',
    pathogenId: DTResultParameterType.UREAPLASMA,
    icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZhbQWzjCgu4jzu8x_CandidaTreatment%2BMed.svg'
  },
  {
    id: TreatmentTypes.Chlamydia,
    name: 'Chlamydia Treatment',
    pathogenId: DTResultParameterType.CHLAMYDIA,
    icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZhbQWzjCgu4jzu8x_CandidaTreatment%2BMed.svg'
  },
  {
    id: TreatmentTypes.Trichomoniasis,
    name: 'Trichomoniasis Treatment',
    pathogenId: DTResultParameterType.TRICHOMONIASIS,
    icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZhbQWjjCgu4jzu8w_TrichoTreatment%2BMed.svg'
  },
  {
    id: TreatmentTypes.MycoplasmaGenitalium,
    name: 'Mycoplasma Genitalium Treatment',
    pathogenId: DTResultParameterType.MYCOPLASMA_GENITALIUM,
    icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZhbQWjjCgu4jzu8w_TrichoTreatment%2BMed.svg'
  }
];
